/**
 * TerminoMunicipal.tsx — Contorno del término municipal propio y sus vecinos en un mapa (VECMUN-c).
 *
 * Reúne lo que comparten los mapas de P2 y P3:
 *  - `grosorContorno` / `estilosContorno`: estilo del límite del propio según el zoom (CONTP3,
 *    sellado por Luis: «amarillo discontinuo», halo oscuro debajo, trazos 3:2 proporcionales al
 *    grosor). Salen de `MapaApoyoCotejo` sin cambiar un valor.
 *  - `ContornoPropio`: solo DIBUJA el contorno; recibe la geometría ya cargada y no pide nada.
 *    P3 le pasa la suya.
 *  - `TerminoMunicipalCapa`: el hijo que `MapViewer` (P2) monta cuando recibe `terminoIne5`:
 *    contorno propio + `VecinosCapa`, con el zoom leído del mapa (`useZoomActual`).
 *
 * P2 no pide el límite del propio (VALMUNI, DOP-2): lo pide P1 o la geolocalización, y aquí se
 * escucha su resultado (`escucharEstadoContorno`) y se lee de la caché (`getBoundaryByINE`) solo
 * cuando el estado es «cargado». Si el límite no llega, no se pinta el propio; los vecinos y sus
 * nombres siguen su camino (VECMUN-b: el que falla no se pinta y el mapa no se ve afectado).
 *
 * Sin casilla para apagarla (DOP c4 de VECMUN-c). Sin glow ni box-shadow (regla dura).
 *
 * @version 1.0.0
 */

import { useEffect, useMemo, useReducer, type FC } from 'react';
import { GeoJSON } from 'react-leaflet';
import {
  escucharEstadoContorno,
  getBoundaryByINE,
} from '@/services/boundaries/MunicipalBoundaryService';
import type { MunicipalBoundary } from '@/lib/spatialValidation';
import { VecinosCapa } from '@/components/wizard-v2/step3-coteja/VecinosCapa';
import { useZoomActual } from './useZoomActual';

// CONTP3 (Luis, «Amarillo discontinuo»): límite administrativo en línea discontinua y color vivo
// que no se confunde con los puntos (rojo, verde, azul). Halo oscuro con el mismo patrón debajo
// para que se lea sobre la ortofoto; los huecos quedan transparentes. Colores fijos, no del tema:
// la base es la ortofoto del PNOA en claro y en oscuro.
// Luis: «la línea debería adaptarse al zoom». Grosor por nivel de zoom (1,5 px con el término
// entero a la vista, 3,5 px de cerca) y trazos/huecos proporcionales al grosor (3:2); el halo,
// 2 px más ancho y con el mismo patrón.
export function grosorContorno(zoom: number): number {
  return Math.min(3.5, Math.max(1.5, 1.5 + (zoom - 10) * 0.5));
}

function estilosContorno(zoom: number) {
  const w = grosorContorno(zoom);
  const dashArray = `${w * 3} ${w * 2}`;
  const comun = { fill: false, dashArray, lineCap: 'butt' as const };
  return {
    halo: { ...comun, color: '#000000', weight: w + 2, opacity: 0.7 },
    linea: { ...comun, color: '#facc15', weight: w, opacity: 1 },
  };
}

export interface ContornoPropioProps {
  /** Contorno ya cargado del municipio propio. */
  boundary: MunicipalBoundary;
  /** Zoom actual del mapa (fija el grosor y el patrón de trazos). */
  zoom: number;
}

/**
 * Halo oscuro discontinuo debajo y línea amarilla discontinua encima, con el mismo patrón de
 * trazos. No interactivo. El estilo es el mismo objeto mientras no cambie el zoom (react-leaflet
 * hace `setStyle` solo cuando cambia la referencia).
 */
export const ContornoPropio: FC<ContornoPropioProps> = ({ boundary, zoom }) => {
  const estilos = useMemo(() => estilosContorno(zoom), [zoom]);
  return (
    <>
      <GeoJSON
        key={`${boundary.codigoINE}-halo`}
        data={boundary.geometry}
        style={estilos.halo}
        interactive={false}
      />
      <GeoJSON
        key={`${boundary.codigoINE}-linea`}
        data={boundary.geometry}
        style={estilos.linea}
        interactive={false}
      />
    </>
  );
};

export interface TerminoMunicipalCapaProps {
  /** Código INE de 5 dígitos del municipio propio (en P2, `ineContorno` de `Step2Page`). */
  ine5: string;
}

/**
 * Término propio y vecinos para el mapa de P2. Se monta dentro del `MapContainer` de `MapViewer`.
 * El apilado lo fijan los panes de `VecinosCapa` (390 y 395, por debajo del 400 donde van el
 * contorno propio y los puntos): nada de esto tapa los puntos del cotejo ni estorba a la cruceta
 * «Ubicar» (todo `interactive={false}`).
 */
export const TerminoMunicipalCapa: FC<TerminoMunicipalCapaProps> = ({ ine5 }) => {
  const zoom = useZoomActual();
  // El contorno propio se lee de la caché en cada render: `getBoundaryByINE` solo devuelve algo
  // cuando el estado es «cargado». Lo único que falta es enterarse de cuándo llega.
  const [, releer] = useReducer((n: number) => n + 1, 0);
  const boundary: MunicipalBoundary | null = getBoundaryByINE(ine5);

  // Solo escucha: P2 no llama a `loadBoundaryByINE` para el propio (VALMUNI DOP-2). Al terminar la
  // petición de P1 o de la geolocalización (o enseguida, si no había ninguna) se vuelve a leer la
  // caché. Guard `vivo` contra el aviso tras desmontaje (HX-UNHANDLED-REJECTION-ENMASCARA-VERDE);
  // el estado no se escribe desde el cuerpo del efecto (`react-hooks/set-state-in-effect`).
  useEffect(() => {
    let vivo = true;
    escucharEstadoContorno(ine5)
      .then(() => { if (vivo) releer(); })
      .catch((err: unknown) => {
        console.warn(`[VECMUN] No se pudo leer el estado del contorno de ${ine5}: ${err instanceof Error ? err.message : String(err)}`);
      });
    return () => { vivo = false; };
  }, [ine5]);

  return (
    <>
      {boundary && <ContornoPropio boundary={boundary} zoom={zoom} />}
      <VecinosCapa ine5={ine5} zoom={zoom} grosor={grosorContorno(zoom)} />
    </>
  );
};

export default TerminoMunicipalCapa;
